<script setup lang="ts">import { computed as _computed } from 'vue';

import { $can } from "@/services/UtilitiesService";
import { useCaseFileStore } from "@/stores/case-file-store";
import { useTagStore } from "@/stores/tags-store";
import { Metadata } from "@/models/Metadata";
import { ModuleStatus } from "@/models/ModuleStatus";
import { useBrandStore } from "@/stores/brand-store";

import type { Tag } from "@/models/Tag";
import type { TagType } from "@/models/TagType";

defineEmits<(e: "enterManagementMode", tagType: TagType) => void>();

const canAnnotate = _computed(() => {
  return $can("annotate");
});

const item = _computed(() => {
  return (useCaseFileStore().currentFile || {}) as Metadata;
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const tags = _computed(() => {
  return useTagStore().tags.filter(
    (t) => t.tagRestriction && t.tagRestriction !== "autoTag" && t.tagRestriction !== "instanceAutoTag",
  );
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const loading = _computed(() => {
  return useTagStore().moduleStatus !== ModuleStatus.Ready;
});

const tagTypes = _computed(() => {
  return useTagStore().tagTypes.filter(
    (r) => r.tagRestriction && r.tagRestriction !== "autoTag" && r.tagRestriction !== "instanceAutoTag",
  );
});

function getTagsByType(tagType: string): Tag[] {
  const fileTags = (useTagStore().tagsObject[item.value.original] || []).filter((f) => f.tagType === tagType);
  return fileTags;
}

function canAddNew(tagType: string): boolean {
  const fileTags = (useTagStore().tagsObject[item.value.original] || []).filter((f) => f.tagType === tagType);
  const fileteredTags: Tag[] = useTagStore().tags.filter(
    (a) => fileTags.findIndex((ft) => ft.tagType === a.tagType) >= 0,
  );
  const tag: TagType | undefined = useTagStore().tagTypes.find((t) => t.name === tagType);
  if (tag === undefined) {
    return false;
  }

  // If it allows multiple, you can have as many as you like
  if (tag.tagRestriction.endsWith("Multiple")) {
    return availableTags(tagType).length > 0;
  }

  // These must be single, you can only have 1
  return fileteredTags.length === 0;
}

function availableTags(tagType: string): Tag[] {
  const fileTags = (useTagStore().tagsObject[item.value.original] || []).filter((f) => f.tagType === tagType);
  const filteredTags: Tag[] = useTagStore().tags.filter(
    (a) => fileTags.findIndex((ft) => ft.name === a.name) < 0 && a.tagType === tagType,
  );
  return filteredTags.sort((l, r) => {
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    });
    return collator.compare(l.name, r.name);
  });
}

function isReadOnly(tagType: string): boolean {
  const config = useBrandStore().viewerConfig.recordList?.userTags ?? [];
  const tagConfig = config.find((c) => c.category === tagType);
  return !!tagConfig?.readOnly;
}

async function onTagSelect(tagType: string, event: Event) {
  const selectElement = event.srcElement as HTMLSelectElement;
  if (!selectElement?.value) {
    return;
  }

  await useTagStore().assignTag({
    fileReference: item.value.original,
    name: selectElement.value,
  });
  selectElement.value = "";
}

function isFixedType(tagType: TagType): boolean {
  if (tagType === null) {
    return false;
  }

  return /^fixed/i.test(tagType.tagRestriction);
}

async function onTagRemove(tag: string) {
  await useTagStore().unAssignTag({ fileReference: item.value.original, name: tag });
}
</script>
<template>
  <div class="tw-divide-y">
    <div
      v-for="(tagType, index) in tagTypes"
      :key="`${tagType.name}=${index}`"
      class="tw-mx-2 tw-grid tw-grid-cols-3 tw-gap-2 tw-py-1"
    >
      <div aria-hidden="true" class="wrapped tw-flex tw-flex-nowrap tw-items-center tw-gap-2">
        {{ tagType.name }}
      </div>
      <div class="wrapped tw-col-span-2 tw-flex tw-gap-1">
        <button
          v-if="canAnnotate && !isFixedType(tagType)"
          type="button"
          class="tw-btn-md-white tw-rounded-sm-r tw-mr-1 tw-border"
          :aria-label="`Edit '${tagType.name}' Tag Type`"
          @click.prevent="$emit('enterManagementMode', tagType)"
        >
          <i aria-hidden="true" class="fas fa-pen" />
        </button>
        <template v-if="canAddNew(tagType.name) && !isReadOnly(tagType.name)">
          <label :for="`${tagType.name}-tagSelect`" class="tw-sr-only">
            {{ tagType.name }}
          </label>
          <select
            :id="`${tagType.name}-tagSelect`"
            v-can:annotate
            class="tw-b-r tw-w-full tw-rounded-md tw-px-2 tw-py-1"
            @change.prevent="onTagSelect(tagType.name, $event)"
          >
            <option value="">Please select ...</option>
            <template v-for="tag in availableTags(tagType.name)" :key="`${tag.name}`">
              <option :value="tag.name">{{ tag.name }}</option>
            </template>
          </select>
        </template>
        <template v-for="(typeTags, idx) in [getTagsByType(tagType.name)]" :key="idx">
          <template v-for="(typeTag, typeTagIdx) in typeTags" :key="typeTag.name">
            <button
              v-if="canAnnotate && !isReadOnly(typeTag.tagType)"
              class="tw-m-1 tw-flex tw-min-w-0 tw-items-center tw-gap-1 hover:tw-opacity-50"
              :aria-label="`Delete Tag ${typeTag.name} of ${tagType.name}`"
              @click.prevent="onTagRemove(typeTag.name)"
            >
              <strong class="tw-truncate">{{ typeTag.name }}</strong>
              <i aria-hidden="true" class="fas fa-times-circle"></i>
            </button>
            <span
              v-for="(content, contentIdx) in [`${typeTag.name}${typeTagIdx + 1 != typeTags.length ? ',' : ''}`]"
              v-else
              :key="contentIdx"
              tabindex="0"
              class="tw-font-bold"
              :aria-label="`${tagType.name}: ${content}`"
            >
              <span class="tw-sr-only">{{ tagType.name }}:</span>
              {{ content }}
            </span>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>
<style scoped>
.text-underline {
  text-decoration: underline;
}
</style>
